import * as Sentry from '@sentry/browser';

import {
  ProcessPaymentResponse,
  ResultActionDto,
} from '@cashier/cashier-api-model';
import {
  AttributeErrorBag,
  InputTypes,
  PaymentActionError,
  PaymentActionErrorContext,
  PaymentActionResult,
  PaymentFormField,
  PaymentStates,
  ResultAction,
  ResultActionContainers,
  ResultActionMethods,
  ResultActionTypes,
} from '@cashier/cashier-model';

interface PaymentActionMapper {
  toResultAction: (dto: ResultActionDto) => ResultAction;
  toPaymentActionResult: (
    response: ProcessPaymentResponse,
  ) => PaymentActionResult;
  toPaymentActionError: (
    response: ProcessPaymentResponse,
  ) => PaymentActionError;
}

const toResultAction = (dto: ResultActionDto): ResultAction => {
  try {
    return {
      type: dto.type as ResultActionTypes,
      url: dto.url,
      method: dto.method as ResultActionMethods,
      parameters: dto.parameters,
      container: dto.container as ResultActionContainers,
      height: dto.height,
      width: dto.width,
      output: dto.output || dto.html,
      fields: dto.fields
        ? dto.fields.map(
            (field) =>
              ({
                ...field,
                type: field.type as InputTypes,
              }) as PaymentFormField,
          )
        : [],
    };
  } catch (error) {
    Sentry.captureException(
      new Error('####Mapping to ResultAction Failure. Data####: '),
      {
        extra: {
          input: dto,
          error,
        },
      },
    );
  }
};

const toAttributeErrorBag = (data: {
  [key: string]: string;
}): AttributeErrorBag | null => {
  const isEmptyObject = (obj: object) => Object.keys(obj).length === 0;

  if (isEmptyObject(data)) return null;

  const errorBag: AttributeErrorBag = {};

  Object.keys(data).forEach((key) => {
    errorBag[key] = [data[key]];
  });

  return errorBag;
};

//@TODO: will be unnecessary after PGW-3023 deploy to PROD - we have to call it eventually only for not empty error.fields (good to see it inside a middleware)
const resolveAttributeErrorBag = (
  response: ProcessPaymentResponse,
): AttributeErrorBag | null => {
  if (response.ui?.display && response.fields?.length) {
    const fieldsToErrorData = Object.fromEntries(
      response.fields.map(({ path, message }) => [path, message]),
    );

    return toAttributeErrorBag(fieldsToErrorData);
  }
  if (response.data) {
    //@TODO: seems like its redundant code
    return toAttributeErrorBag(response.data);
  }
  return response.errors || null;
};

export const PaymentActionMapper: PaymentActionMapper = {
  toResultAction: (dto: ResultActionDto): ResultAction => toResultAction(dto),
  toPaymentActionResult: (
    response: ProcessPaymentResponse,
  ): PaymentActionResult => {
    const resultAction = response.resultAction || response.redirectOutput;

    try {
      return {
        transactionId: response.transactionId,
        status: (response.status || response.transactionState) as PaymentStates,
        resultAction: resultAction ? toResultAction(resultAction) : null,
        description: response.description || null,
        paymentAttributes: response.paymentAttributes
          ? response.paymentAttributes.map(
              (field) =>
                ({
                  ...field,
                  type: field.type as InputTypes,
                }) as PaymentFormField,
            )
          : [],
      };
    } catch (error) {
      Sentry.captureException(
        new Error('####Mapping to PaymentAction Result Failure. Data####: '),
        {
          extra: {
            input: response,
            error,
          },
        },
      );
    }
  },
  toPaymentActionError: (
    response: ProcessPaymentResponse,
  ): PaymentActionError => {
    try {
      //@TODO: will be unnecessary after PGW-3023 deploy to PROD
      const context: PaymentActionErrorContext | null = [
        response.ui,
        response.fields,
        response.payment,
      ].every((item) => item !== undefined)
        ? {
            ui: response.ui,
            fields: response.fields,
            payment: response.payment,
          }
        : null;

      return {
        message:
          response.ui?.message ||
          response.message ||
          response.error ||
          'Something went wrong.',
        attributeErrorBag: resolveAttributeErrorBag(response),
        errorDetails: response.errorDetails || null,
        transactionId: response.transactionId || response.payment?.id || null,
        context,
      };
    } catch (error) {
      Sentry.captureException(
        new Error('####Mapping to PaymentAction Error Failure. Data####: '),
        {
          extra: {
            input: response,
            error,
          },
        },
      );
    }
  },
};
